// Tenant feature flags (plan 041) — the client half of server/services/featureRegistry.js.
// DEFAULT_FEATURES mirrors each registry feature's `default`: the shape used
// while loading, on error, or when logged out, so nothing default-on vanishes
// on a blip and nothing default-off appears.
export const DEFAULT_FEATURES = Object.freeze({
  payroll: true,
  benefits: true,
  time_off: true,
  pipeline: false,
  tasks: false,
  departments: false,
});

/**
 * Payroll tab visibility (plan 041, CPA): while Payroll is off a tenant with
 * payroll history keeps My Pay (stubs + W-2s, never for contractors) and the
 * Payroll / Tax Filing / Tax Deposits tabs in read-only mode. Deductions and YTD Import need
 * Payroll on. Returns undefined for labels this rule does not own.
 */
export function payrollTabVisible(label, features, { isContractor = false } = {}) {
  const records = !!(features.payroll || features.payroll_history);
  if (label === 'My Pay') return records && !isContractor;
  if (label === 'Payroll' || label === 'Tax Filing' || label === 'Tax Deposits') return records;
  if (label === 'Deductions' || label === 'YTD Import') return !!features.payroll;
  return undefined;
}

/** Payroll is off but its records stay visible: screens hide every write. */
export function isPayrollReadOnly(features) {
  return !features.payroll && !!features.payroll_history;
}

export const PAYROLL_READ_ONLY_TEXT = 'Payroll is turned off — past runs, pay stubs and tax forms stay available read-only';

/**
 * Normalize a GET /tenant-settings/features body.
 * Only known keys with boolean values override the defaults.
 * @returns {{ features: object, employeeHome: 'full'|'clock', catalog: Array, accrualWarning: string|null }}
 */
export function normalizeFeatures(data) {
  const features = { ...DEFAULT_FEATURES };
  const raw = data && typeof data.features === 'object' && data.features ? data.features : {};
  Object.keys(DEFAULT_FEATURES).forEach((key) => {
    if (typeof raw[key] === 'boolean') features[key] = raw[key];
  });
  // Not a registry switch: the tenant has payroll records (a processed run or
  // a current/prior-year W-2) that stay reachable while Payroll is off.
  features.payroll_history = data?.payroll_history === true;
  return {
    features,
    employeeHome: data?.employee_home === 'clock' ? 'clock' : 'full',
    catalog: Array.isArray(data?.catalog) ? data.catalog : [],
    // Admin-only server nudge (Payroll-off PTO accrual has no future pay period)
    accrualWarning: typeof data?.accrual_warning === 'string' && data.accrual_warning ? data.accrual_warning : null,
  };
}
